import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus } from "lucide-react";
import { useContent } from "@/content";
import { Reveal, EASE } from "@/components/Motion";
import { V3Section, V3Eyebrow, V3Title } from "@/sections/v3/shared";

// 2. Essence — citation typographique, aucune photo.
export const Essence3 = () => {
  const e = useContent().essence;
  return (
    <V3Section id={e.id} bg="#141011" className="py-24 md:py-36">
      <V3Eyebrow>{e.eyebrow}</V3Eyebrow>
      <div className="relative mt-14">
        <span aria-hidden="true" className="pointer-events-none absolute -top-[5.5rem] -start-3 select-none font-serif text-[9rem] leading-none text-plum/50 md:-start-10 md:text-[13rem]">
          &ldquo;
        </span>
        <blockquote className="relative">
          <Reveal>
            <p className="max-w-4xl font-serif text-[1.55rem] italic leading-[1.3] text-white md:text-[2.2rem]" data-testid="essence-quote">
              {e.paragraphs[0]}
            </p>
          </Reveal>
        </blockquote>
      </div>
      <Reveal delay={0.15} className="v3-cols mt-16 max-w-5xl text-[0.95rem] leading-relaxed text-white/80 md:mt-20">
        <p>{e.paragraphs[1]}</p>
        <p>{e.paragraphs[2]}</p>
      </Reveal>
    </V3Section>
  );
};

// 3. Pourquoi participer — accordéon de grandes lignes, chapeau sticky à gauche.
const WhyRow = ({ item, index, open, onToggle }) => (
  <div className="border-t border-copper/25 last:border-b" data-testid={`why-item-${index + 1}`}>
    <h3>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={`why-panel-${index}`}
        data-testid={`why-toggle-${index + 1}`}
        className="flex w-full items-center justify-between gap-6 py-6 text-start md:py-7"
      >
        <span className={`font-serif text-[1.25rem] leading-snug transition-colors duration-300 md:text-[1.6rem] ${open ? "text-blush" : "text-white"}`}>
          {item.title}
        </span>
        <span className="flex shrink-0 items-center gap-5 md:gap-8">
          <span className="font-sans text-[0.75rem] tabular-nums tracking-[0.2em] text-copper" aria-hidden="true">
            {String(index + 1).padStart(2, "0")}
          </span>
          <motion.span animate={{ rotate: open ? 45 : 0 }} transition={{ duration: 0.5, ease: EASE }} className="text-copper">
            <Plus size={22} strokeWidth={1} />
          </motion.span>
        </span>
      </button>
    </h3>
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          id={`why-panel-${index}`}
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.55, ease: EASE }}
          className="overflow-hidden"
          data-testid={`why-panel-${index + 1}`}
        >
          <p className="max-w-3xl pb-8 text-[0.95rem] leading-relaxed text-white/80">{item.text}</p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

export const Why3 = () => {
  const w = useContent().why;
  const [open, setOpen] = useState(0); // la première ligne est ouverte par défaut
  return (
    <V3Section id={w.id} bg="#0B0A0A" className="py-24 md:py-32">
      <div className="grid gap-14 lg:grid-cols-[minmax(230px,300px)_1fr] lg:gap-24">
        <div className="lg:sticky lg:top-24 lg:self-start">
          <V3Eyebrow>{w.eyebrow}</V3Eyebrow>
          <V3Title testid="why-title" className="md:text-[2rem]">{w.title}</V3Title>
          <Reveal delay={0.15}>
            <p className="mt-6 text-[0.95rem] leading-relaxed text-white/75">{w.intro}</p>
          </Reveal>
        </div>
        <div data-testid="why-list">
          {w.items.map((it, i) => (
            <WhyRow key={it.title} item={it} index={i} open={open === i} onToggle={() => setOpen(open === i ? -1 : i)} />
          ))}
        </div>
      </div>
    </V3Section>
  );
};

// 4. Qui peut participer — grands tags en flux, puis FAQ fine.
const FaqRow = ({ q, a, i }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-t border-copper/25 last:border-b" data-testid={`faq-item-${i}`}>
      <h3>
        <button
          type="button"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          aria-controls={`faq-panel-${i}`}
          data-testid={`faq-toggle-${i}`}
          className="flex w-full items-center justify-between gap-6 py-5 text-start"
        >
          <span className="font-serif text-[1.05rem] leading-snug text-white md:text-[1.2rem]">{q}</span>
          <motion.span animate={{ rotate: open ? 45 : 0 }} transition={{ duration: 0.5, ease: EASE }} className="shrink-0 text-copper">
            <Plus size={18} strokeWidth={1} />
          </motion.span>
        </button>
      </h3>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            id={`faq-panel-${i}`}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="overflow-hidden"
            data-testid={`faq-answer-${i}`}
          >
            <p className="max-w-3xl pb-6 text-[0.95rem] leading-relaxed text-white/80">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const Who3 = () => {
  const w = useContent().who;
  return (
    <V3Section id={w.id} bg="#1A1213" className="py-24 md:py-32">
      <V3Eyebrow>{w.eyebrow}</V3Eyebrow>
      <V3Title testid="who-title">{w.title}</V3Title>
      <Reveal delay={0.1}>
        <p className="mt-6 max-w-2xl text-[0.95rem] leading-relaxed text-white/75">{w.intro}</p>
      </Reveal>
      <Reveal delay={0.15}>
        <p className="mt-14 text-[0.75rem] uppercase tracking-[0.3em] text-copper">{w.listTitle}</p>
      </Reveal>
      <ul className="mt-6 flex flex-wrap gap-3" data-testid="who-list">
        {w.list.map((l, i) => (
          <Reveal as="li" key={l} delay={i * 0.03}>
            <span className="inline-block border border-copper/40 px-5 py-3 font-serif text-[1.15rem] leading-snug text-white/90 transition-colors duration-300 hover:border-plum hover:bg-plum hover:text-white">
              {l}
            </span>
          </Reveal>
        ))}
      </ul>
      <div className="mt-16 max-w-4xl" data-testid="faq">
        {w.faq.map((f, i) => (
          <FaqRow key={i} i={i} {...f} />
        ))}
      </div>
      <Reveal className="mt-10 max-w-3xl">
        <p className="text-[0.95rem] leading-relaxed text-white/70">{w.note}</p>
        <p className="mt-3 text-[0.95rem] leading-relaxed text-white/70">{w.nb}</p>
      </Reveal>
    </V3Section>
  );
};
